/**
 * The Quarantine-by-reason section (REQ-STATUS-100).
 *
 * # What it is for
 *
 * The pane's Domain metrics already carry `quarantined_total` — *that* messages
 * were refused, and at what rate. REQ-STATUS-097 obliges the ingest-side metrics
 * to be "diagnosable from the System display alone", and its Notes claimed
 * quarantine was counted **per reason** there. It was not: the per-reason counter
 * existed only as a Prometheus label, readable in Grafana and nowhere in the
 * product. This section is that half.
 *
 * # Two numbers, one screen, and why each says its own scope
 *
 * `quarantined_total` is what the *process* has done since it started. The
 * `total` here is what the drain *persisted* inside the selected window. They
 * disagree by construction and neither is derivable from the other, so each row
 * group states its own scope rather than presenting a figure that looks like it
 * ought to reconcile with the metric a few lines above.
 *
 * # Absence is never rendered as zero
 *
 * Three outcomes arrive on the same field and must read differently
 * (REQ-STATUS-016; CLAUDE.md §8.1 fail-safe-not-fail-open):
 *
 *   - **A measured zero** — "no quarantines in this window" — is a real and
 *     reassuring statement, and is shown as one.
 *   - **`read_failed`** means we do not know. Shown as not-known; a "0" here
 *     would turn a database blip into a clean bill of health.
 *   - **`not_an_ingest_component`** means the question does not apply. Normally
 *     unreachable, because the caller only mounts this section for
 *     {@link PROVIDER_KINDS}; it survives as an explicit line for the case the
 *     control plane no longer resolves the id (a renamed interface).
 *
 * Cross-references:
 *   - REQ-STATUS-100 (the read), REQ-STATUS-097 (the obligation it closes).
 *   - REQ-QUERY-058 (the queryable type each row links to), REQ-UI-189 (the
 *     cross-pane handoff mechanism).
 *   - `status-trend-section` — the sibling windowed section this mirrors, and
 *     the owner of the shared window state.
 */
import { useEffect, useState } from "react";
import { SectionShell } from "@/entity-details/section-shell";
import { usePaneHistoryWindow } from "@/entity-details/use-pane-history-window";
import { useEntityDetailsSectionsContext } from "@/entity-details/entity-details-sections-context";
import {
  HISTORY_WINDOWS,
  HISTORY_WINDOW_LABELS,
  type HistoryWindow,
} from "@/system-status/use-component-history";
import {
  QUARANTINE_TYPE_ID,
  QUARANTINE_UNAVAILABLE,
  type QuarantineReason,
  useQuarantineReasons,
} from "@/system-status/use-quarantine-reasons";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { useQueryHandoffStore } from "@/query/query-handoff-store";
import { formatRecency } from "@/entity-details/quarantine-recency";

/**
 * How often a visible row's "… ago" is recomputed.
 *
 * Thirty seconds, not one: the finest bucket this renders is seconds-ago on a
 * very fresh quarantine, and a per-second tick would re-render the whole section
 * to redraw text that is usually identical.
 */
const RECENCY_TICK_MS = 30_000;

/** The section's own id — the key its collapse state persists under. */
export const QUARANTINE_SECTION_ID = "status-quarantine";

/**
 * One reason row, with a link into the records behind its count.
 *
 * # The link seeds the REASON only, and says so
 *
 * It would be better to also scope the handoff to this pane's interfaces, and it
 * is deliberately not attempted: the queryable attribute is `interface_id`, which
 * holds the interface **UUID**, while this pane only ever knows the component's
 * NAME (the status wire's own identifier). Seeding it would mean either putting
 * the UUIDs on the breakdown response or teaching the handoff an `in` operator —
 * both larger than the fidelity they buy, because the Query panel *shows* the
 * chips it was handed, so the operator can see the scope they landed on and
 * narrow it.
 *
 * What that rules out is a label implying otherwise: the button says "all", not
 * "this provider's".
 */
function ReasonRow({ reason, now }: { reason: QuarantineReason; now: number }): React.ReactElement {
  const requestQuery = useQueryHandoffStore((state) => state.requestQuery);
  const multiPaneApi = useMultiPaneApi();
  return (
    <>
      <dt className="truncate text-text-secondary" title={reason.reason}>
        {reason.reason}
      </dt>
      <dd className="flex items-baseline justify-end gap-2 font-mono tabular-nums">
        <span
          className="text-text-muted"
          // The absolute instant for the operator who needs it, without
          // spending the narrow column on it.
          title={reason.last_at}
          data-testid={`quarantine-reason-recency-${reason.reason}`}
        >
          {formatRecency(reason.last_at, now)}
        </span>
        <span data-testid={`quarantine-reason-count-${reason.reason}`}>{reason.count}</span>
        <button
          type="button"
          onClick={() => {
            requestQuery({
              typeId: QUARANTINE_TYPE_ID,
              predicates: [{ attribute: "reason", value: reason.reason }],
            });
            multiPaneApi?.activatePanel("query");
          }}
          data-testid={`quarantine-reason-query-${reason.reason}`}
          // "all", not "these" — the handoff carries the reason and not this
          // pane's scope (see the note above), and the label must not promise
          // a narrower result than the panel will show.
          aria-label={`Query all ${reason.reason} quarantined messages`}
          className="rounded border border-border px-1.5 text-caption text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          Query
        </button>
      </dd>
    </>
  );
}

export function StatusQuarantineSection({
  componentId,
  entityLabel,
}: {
  componentId: string | null;
  /** What the breakdown is *of* — used in the accessible names. */
  entityLabel: string;
}): React.ReactElement {
  // Named `historyWindow`, not `window`: this component needs the DOM global
  // for its recency timer, and shadowing it is how that becomes a puzzle.
  const [historyWindow, setHistoryWindow] = usePaneHistoryWindow();
  const sections = useEntityDetailsSectionsContext();
  // A collapsed section issues no request, matching REQ-STATUS-072's rule for
  // the Trend section. A context-less caller (an isolated test) reads as open,
  // matching SectionShell.
  const expanded = sections === null ? true : sections.sections.isExpanded(QUARANTINE_SECTION_ID);
  const breakdown = useQuarantineReasons({
    componentId,
    window: historyWindow,
    enabled: expanded,
  });
  const data = breakdown.data;

  // One timestamp for every row's recency, so two rows in the same paint cannot
  // disagree about what "now" is — and read through state rather than calling
  // `Date.now()` in the render body, which `react-hooks/purity` rejects because
  // a clock read makes render non-deterministic. The `Clocks` component uses the
  // same lazy-initialiser-plus-interval shape.
  const [now, setNow] = useState<number>(() => Date.now());
  // Only armed while there is a row whose age is on screen: a collapsed or empty
  // section has nothing to age, so it should not hold a timer.
  const hasRows = (data?.reasons.length ?? 0) > 0;
  useEffect(() => {
    if (!hasRows) return;
    const id = window.setInterval(() => setNow(Date.now()), RECENCY_TICK_MS);
    return () => window.clearInterval(id);
  }, [hasRows]);

  return (
    <SectionShell id={QUARANTINE_SECTION_ID} title="Quarantine by reason">
      <div className="flex flex-col gap-3" data-testid="status-quarantine-body">
        <label className="flex items-center gap-2 text-meta text-text-secondary">
          <span>Window</span>
          <select
            value={historyWindow}
            onChange={(event) => setHistoryWindow(event.target.value as HistoryWindow)}
            data-testid="quarantine-window"
            aria-label={`Quarantine window for ${entityLabel}`}
            className="rounded border border-border bg-bg-base px-1.5 py-0.5 text-meta text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            {HISTORY_WINDOWS.map((option) => (
              <option key={option} value={option}>
                {HISTORY_WINDOW_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        {breakdown.isPending ? (
          <p className="text-body-sm text-text-muted">Loading…</p>
        ) : breakdown.isError ? (
          // An error is NOT an empty breakdown. Saying "could not be read" is
          // the honest line; "no quarantines" would be a false green.
          <p className="text-body-sm text-status-warning" data-testid="quarantine-error">
            Quarantine counts could not be read.
          </p>
        ) : data === undefined ? null : data.unavailable === QUARANTINE_UNAVAILABLE.readFailed ? (
          <p className="text-body-sm text-status-warning" data-testid="quarantine-unavailable">
            Quarantine counts could not be read — this is not a count of zero.
          </p>
        ) : data.unavailable === QUARANTINE_UNAVAILABLE.notAnIngestComponent ? (
          <p className="text-body-sm text-text-muted" data-testid="quarantine-not-applicable">
            Not an ingest interface — no quarantine record applies.
          </p>
        ) : data.reasons.length === 0 ? (
          // A measured zero, and worth saying plainly: the operator asked and
          // the answer is "nothing was refused".
          <p className="text-body-sm text-text-muted" data-testid="quarantine-none">
            No messages quarantined in the {HISTORY_WINDOW_LABELS[historyWindow]}.
          </p>
        ) : (
          <>
            <dl className="grid grid-cols-[1fr_max-content] gap-x-4 gap-y-1 text-body-sm">
              {data.reasons.map((reason) => (
                <ReasonRow key={reason.reason} reason={reason} now={now} />
              ))}
            </dl>
            <p className="text-meta text-text-muted" data-testid="quarantine-scope-note">
              {data.total} quarantined in the {HISTORY_WINDOW_LABELS[historyWindow]}
              {data.truncated > 0
                ? `, across ${data.reasons.length + data.truncated} reasons (${data.truncated} not shown)`
                : ""}
              {". "}
              {/* The reconciliation note. Without it an operator reads this
                  against `quarantined_total` a few rows up and finds two
                  numbers that will not add up, because they count different
                  things over different spans. */}
              Counted from stored records; the Domain metrics{" "}
              <span className="font-mono">quarantined_total</span> counts every quarantine since
              each process started.
            </p>
          </>
        )}
      </div>
    </SectionShell>
  );
}
